import { useState, useEffect } from 'react';
import axios from 'axios';
import { Headphones, Send, Clock, CheckCircle, AlertCircle, MessageSquare, Loader2, ChevronDown } from 'lucide-react';
import SiteHeader from '@/components/SiteHeader';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { API } from '@/App';
import { toast } from 'sonner';

const CATEGORIES = [
  { value: 'general', label: 'Question générale' },
  { value: 'account', label: 'Mon compte / Adhésion' },
  { value: 'technical', label: 'Problème technique' },
  { value: 'content', label: 'Contenu / Formations' },
  { value: 'other', label: 'Autre' },
];

const STATUS_MAP = {
  open: { label: 'Ouvert', color: 'bg-blue-100 text-blue-700', icon: Clock },
  in_progress: { label: 'En cours', color: 'bg-amber-100 text-amber-700', icon: AlertCircle },
  resolved: { label: 'Résolu', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  closed: { label: 'Fermé', color: 'bg-gray-100 text-gray-600', icon: CheckCircle },
};

const SupportPage = () => {
  const [form, setForm] = useState({ title: '', description: '', category: 'general', priority: 'medium' });
  const [submitting, setSubmitting] = useState(false);
  const [myTickets, setMyTickets] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [expandedTicket, setExpandedTicket] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      setIsLoggedIn(true);
      fetchMyTickets(token);
    }
  }, []);

  const fetchMyTickets = async (token) => {
    setLoadingTickets(true);
    try {
      const res = await axios.get(`${API}/tickets`, { headers: { Authorization: `Bearer ${token}` } });
      setMyTickets(res.data);
    } catch {}
    setLoadingTickets(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.description.trim()) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Vous devez être connecté pour envoyer un ticket');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/tickets`, form, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Ticket envoyé avec succès !');
      setForm({ title: '', description: '', category: 'general', priority: 'medium' });
      fetchMyTickets(token);
    } catch {
      toast.error("Erreur lors de l'envoi du ticket");
    }
    setSubmitting(false);
  };

  const formatDate = (d) => {
    try { return new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return d; }
  };

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Roboto', sans-serif" }} data-testid="support-page">
      <SEO title="Support Technique - SFOALC" description="Contactez le support technique de la SFOALC" />
      <SiteHeader />

      {/* Hero */}
      <section className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #1e3a5f 0%, #2a6b8f 50%, #35AFE4 100%)' }}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-40 w-72 h-72 rounded-full bg-white/20" />
          <div className="absolute -bottom-20 -left-20 w-60 h-60 rounded-full bg-white/10" />
        </div>
        <div className="max-w-5xl mx-auto px-4 pt-28 pb-16 text-center relative z-10">
          <p className="text-[#8ec8e0] text-xs font-bold uppercase tracking-[0.25em] mb-4">Assistance</p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4" style={{ fontFamily: "'Barlow', sans-serif" }}>
            Support Technique
          </h1>
          <p className="text-white/70 text-sm sm:text-base max-w-2xl mx-auto leading-relaxed">
            Une question, un problème ? Notre équipe est là pour vous aider.
          </p>
        </div>
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1000 60" preserveAspectRatio="none" style={{ display: 'block', width: '100%', height: '40px' }}>
            <path fill="#ffffff" d="M0,60V50C0.9,33,126.7,0,500,0S999.1,33,1000,50V60H0Z" />
          </svg>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">

          {/* Formulaire — 3 colonnes */}
          <div className="lg:col-span-3" data-testid="support-form-section">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 sm:p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-[#35AFE4] flex items-center justify-center">
                  <Send size={18} className="text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-[#1e3a5f]" style={{ fontFamily: "'Barlow', sans-serif" }}>Envoyer un ticket</h2>
                  <p className="text-xs text-gray-400">Nous vous répondrons dans les meilleurs délais</p>
                </div>
              </div>

              {!isLoggedIn ? (
                <div className="text-center py-8">
                  <Headphones size={32} className="mx-auto mb-3 text-gray-300" />
                  <p className="text-sm text-gray-500 mb-3">Vous devez être connecté pour envoyer un ticket de support.</p>
                  <a href="/auth" className="inline-flex items-center px-5 py-2.5 bg-[#1e3a5f] text-white text-sm font-semibold rounded-full hover:bg-[#2a5a8f] transition-colors" data-testid="login-link">
                    Se connecter
                  </a>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Catégorie</label>
                      <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#35AFE4] focus:border-transparent outline-none" data-testid="category-select">
                        {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Priorité</label>
                      <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#35AFE4] focus:border-transparent outline-none" data-testid="priority-select">
                        <option value="low">Basse</option>
                        <option value="medium">Moyenne</option>
                        <option value="high">Haute</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Sujet</label>
                    <input type="text" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
                      placeholder="Résumez votre demande en quelques mots"
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#35AFE4] focus:border-transparent outline-none" data-testid="title-input" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Description</label>
                    <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                      rows={5} placeholder="Décrivez votre problème ou question en détail..."
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#35AFE4] focus:border-transparent outline-none resize-none" data-testid="description-input" />
                  </div>
                  <button type="submit" disabled={submitting}
                    className="w-full py-3 bg-[#1e3a5f] hover:bg-[#2a5a8f] text-white font-semibold text-sm rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-60" data-testid="submit-ticket-btn">
                    {submitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                    {submitting ? 'Envoi...' : 'Envoyer le ticket'}
                  </button>
                </form>
              )}
            </div>
          </div>

          {/* Mes tickets — 2 colonnes */}
          <div className="lg:col-span-2" data-testid="my-tickets-section">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl bg-[#eaf6fc] flex items-center justify-center">
                  <MessageSquare size={18} className="text-[#35AFE4]" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-[#1e3a5f]" style={{ fontFamily: "'Barlow', sans-serif" }}>Mes tickets</h2>
                  <p className="text-xs text-gray-400">{myTickets.length} ticket(s)</p>
                </div>
              </div>

              {!isLoggedIn ? (
                <p className="text-sm text-gray-400 text-center py-6">Connectez-vous pour voir vos tickets</p>
              ) : loadingTickets ? (
                <div className="flex justify-center py-8"><Loader2 size={24} className="animate-spin text-[#35AFE4]" /></div>
              ) : myTickets.length === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle size={28} className="mx-auto mb-2 text-gray-200" />
                  <p className="text-sm text-gray-400">Aucun ticket pour le moment</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[500px] overflow-y-auto pr-1">
                  {myTickets.map(t => {
                    const st = STATUS_MAP[t.status] || STATUS_MAP.open;
                    const StIcon = st.icon;
                    const isExpanded = expandedTicket === t.id;
                    return (
                      <div key={t.id} className="border border-gray-100 rounded-xl overflow-hidden hover:border-gray-200 transition-colors" data-testid={`ticket-${t.id}`}>
                        <button onClick={() => setExpandedTicket(isExpanded ? null : t.id)}
                          className="w-full flex items-center gap-3 p-3 text-left">
                          <StIcon size={14} className={st.color.includes('blue') ? 'text-blue-500' : st.color.includes('amber') ? 'text-amber-500' : 'text-emerald-500'} />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-800 truncate">{t.title}</p>
                            <p className="text-[10px] text-gray-400">{formatDate(t.created_at)}</p>
                          </div>
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${st.color}`}>{st.label}</span>
                          <ChevronDown size={14} className={`text-gray-300 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                        </button>
                        {isExpanded && (
                          <div className="px-3 pb-3 border-t border-gray-50">
                            <p className="text-xs text-gray-600 mt-2 whitespace-pre-wrap">{t.description}</p>
                            {t.response && (
                              <div className="mt-3 p-3 bg-[#f4fafe] rounded-lg border border-[#d0e8f0]">
                                <p className="text-[10px] font-bold text-[#35AFE4] mb-1 uppercase">Réponse de l'équipe</p>
                                <p className="text-xs text-gray-700">{t.response}</p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default SupportPage;
